import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { HiChevronDown, HiXMark, HiCheck } from 'react-icons/hi2'
import { cnMerge } from '../../utils/twmerge'
import type { UserMultiSelectProps, UserOption } from './types'

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export const UserMultiSelect: React.FC<UserMultiSelectProps> = ({
  options,
  value = [],
  onChange,
  placeholder = "Selecione usuários...",
  className,
  disabled = false,
  searchable = true,
  onSearch,
  maxDisplayItems = 2
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOptions, setSelectedOptions] = useState<UserOption[]>(
    options.filter(opt => value.includes(opt.value))
  )
  const [showPopup, setShowPopup] = useState(false)
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 })
  const selectRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const popupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Filtrar opções por busca
  const searchFilteredOptions = searchable && searchQuery
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options

  // Ordenar colocando selecionados no topo
  const filteredOptions = searchFilteredOptions.sort((a, b) => {
    const aSelected = selectedOptions.some(opt => opt.value === a.value)
    const bSelected = selectedOptions.some(opt => opt.value === b.value)
    
    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1
    return 0
  })

  // Sincronizar selectedOptions quando value mudar externamente
  useEffect(() => {
    const newSelectedOptions = options.filter(opt => value.includes(opt.value))
    setSelectedOptions(newSelectedOptions)
  }, [value, options])

  // Calcular posição do dropdown quando aberto
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect()
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }
      
      const dropdownHeight = Math.min(280, filteredOptions.length * 56 + 40 + 8) // 56px per user option + search input + padding
      const spaceBelow = viewport.height - rect.bottom
      const spaceAbove = rect.top
      
      let top = rect.bottom + window.scrollY + 4 // 4px gap (mt-1)
      
      // Se não há espaço suficiente abaixo, mostrar acima
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight - 4
      }
      
      setDropdownCoords({
        top,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [isOpen, filteredOptions.length])

  // Fechar dropdown e popup quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current && 
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
        setShowPopup(false)
        if (popupTimeoutRef.current) {
          clearTimeout(popupTimeoutRef.current)
          popupTimeoutRef.current = null
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        if (popupTimeoutRef.current) {
          clearTimeout(popupTimeoutRef.current)
        }
      }
    }
    
    return () => {
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current)
      }
    }
  }, [isOpen])

  // Focar no input de busca quando abrir
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  const handleToggleOption = (option: UserOption) => {
    if (disabled) return

    const isSelected = selectedOptions.some(opt => opt.value === option.value)
    let newSelectedOptions: UserOption[]
    let newValues: (string | number)[]

    if (isSelected) {
      // Remover opção
      newSelectedOptions = selectedOptions.filter(opt => opt.value !== option.value)
      newValues = newSelectedOptions.map(opt => opt.value)
    } else {
      // Adicionar opção
      newSelectedOptions = [...selectedOptions, option]
      newValues = newSelectedOptions.map(opt => opt.value)
    }

    setSelectedOptions(newSelectedOptions)
    onChange?.(newValues)

    if (onSearch) {
      onSearch(searchQuery)
    }
  }

  const handleRemoveOption = (optionToRemove: UserOption) => {
    if (disabled) return

    const newSelectedOptions = selectedOptions.filter(opt => opt.value !== optionToRemove.value)
    const newValues = newSelectedOptions.map(opt => opt.value)
    
    setSelectedOptions(newSelectedOptions)
    onChange?.(newValues)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)
    if (onSearch) {
      onSearch(query)
    }
  }

  const displayedOptions = selectedOptions.slice(0, maxDisplayItems)
  const hiddenCount = selectedOptions.length - maxDisplayItems

  return (
    <>
      <div 
        ref={selectRef}
        className={cnMerge("relative", className)}
    >
      {/* Trigger Container */}
      <div
        className={cnMerge(
          'flex h-8 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-colors cursor-pointer relative',
          'dark:border-neutral-600 dark:bg-neutral-800 dark:text-gray-100',
          'hover:border-gray-400 dark:hover:border-neutral-500 focus-within:border-blue-500 focus-within:outline-none focus-within:ring-1 focus-within:ring-blue-500',
          disabled && 'cursor-not-allowed bg-gray-50 dark:bg-neutral-700 text-gray-500 dark:text-gray-400',
          isOpen && 'border-blue-500 ring-1 ring-blue-500'
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {selectedOptions.length > 0 ? (
            <div className="flex items-center space-x-1 flex-wrap gap-1">
              {/* Avatares dos usuários selecionados */}
              <div 
                className="flex -space-x-1 relative"
                onMouseEnter={() => {
                  if (selectedOptions.length > 0) {
                    if (popupTimeoutRef.current) {
                      clearTimeout(popupTimeoutRef.current)
                      popupTimeoutRef.current = null
                    }
                    setShowPopup(true)
                  }
                }}
                onMouseLeave={() => {
                  popupTimeoutRef.current = setTimeout(() => {
                    setShowPopup(false)
                  }, 150)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setShowPopup(!showPopup)
                }}
              >
                {displayedOptions.map((option) => (
                  <div 
                    key={option.value}
                    className="relative group"
                  >
                    {option.avatar ? (
                      <img
                        src={option.avatar}
                        alt={option.name || option.label}
                        className="h-6 w-6 rounded-full object-cover border border-white cursor-pointer"
                      />
                    ) : (
                      <div 
                        className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs font-medium text-white border border-white cursor-pointer"
                      >
                        {getInitials(option.name || option.label)}
                      </div>
                    )}
                    {/* Botão X para remover */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveOption(option)
                      }}
                      className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                    >
                      <HiXMark className="w-2 h-2" />
                    </div>
                  </div>
                ))}
                {hiddenCount > 0 && (
                  <div className="h-6 w-6 bg-gray-400 rounded-full flex items-center justify-center border border-white cursor-pointer">
                    <span className="text-white text-xs font-medium">
                      +{hiddenCount}
                    </span>
                  </div>
                )}

                {/* Popup com informações detalhadas */}
                {showPopup && selectedOptions.length > 0 && (
                  <div 
                    className="absolute top-8 left-0 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50 min-w-64 max-w-80"
                    onMouseEnter={() => {
                      if (popupTimeoutRef.current) {
                        clearTimeout(popupTimeoutRef.current)
                        popupTimeoutRef.current = null
                      }
                    }}
                    onMouseLeave={() => {
                      popupTimeoutRef.current = setTimeout(() => {
                        setShowPopup(false)
                      }, 150)
                    }}
                  >
                    <div className="text-xs font-medium text-gray-700 mb-2">
                      {selectedOptions.length} usuário{selectedOptions.length > 1 ? 's' : ''} selecionado{selectedOptions.length > 1 ? 's' : ''}:
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedOptions.map((option) => (
                        <div key={option.value} className="flex items-center space-x-2 p-1">
                          {option.avatar ? (
                            <img
                              src={option.avatar}
                              alt={option.name || option.label}
                              className="h-5 w-5 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs font-medium text-white flex-shrink-0">
                              {getInitials(option.name || option.label)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 truncate">
                              {option.name || option.label}
                            </div>
                            {option.email && (
                              <div className="text-xs text-gray-500 truncate">
                                {option.email}
                              </div>
                            )}
                          </div>
                          <div
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRemoveOption(option)
                            }}
                            className="text-red-500 hover:text-red-700 flex-shrink-0 cursor-pointer"
                          >
                            <HiXMark className="w-3 h-3" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Contador */}
              <span className="text-xs text-gray-500 ml-2">
                {selectedOptions.length} selecionado{selectedOptions.length > 1 ? 's' : ''}
              </span>
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        
        <HiChevronDown 
          className={cnMerge(
            "h-4 w-4 text-gray-400 transition-transform",
            isOpen && "rotate-180"
          )} 
        />
      </div>

      </div>

      {/* Dropdown usando Portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] rounded-md border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-lg"
          style={{
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width}px`
          }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="p-2 border-b border-gray-100 dark:border-neutral-700">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Buscar usuários..."
                className="w-full rounded border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}
          
          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = selectedOptions.some(opt => opt.value === option.value)
                
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleToggleOption(option)}
                    disabled={option.disabled}
                    className={cnMerge(
                      'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors',
                      'hover:bg-gray-50 dark:hover:bg-neutral-700 focus:bg-gray-50 dark:focus:bg-neutral-700 focus:outline-none',
                      option.disabled && 'cursor-not-allowed opacity-50',
                      isSelected && 'bg-blue-50 dark:bg-blue-900/30'
                    )}
                  >
                    {/* Checkbox */}
                    <div className={cnMerge(
                      "flex h-4 w-4 items-center justify-center rounded border",
                      isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300 dark:border-neutral-600"
                    )}>
                      {isSelected && <HiCheck className="h-3 w-3 text-white" />}
                    </div>
                    
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {option.avatar ? (
                        <img
                          src={option.avatar}
                          alt={option.name || option.label}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 dark:bg-neutral-600 text-xs font-medium text-gray-600 dark:text-gray-300">
                          {getInitials(option.name || option.label)}
                        </div>
                      )}
                    </div>
                    
                    {/* User Info */}
                    <div className="flex flex-col">
                      <span className={cnMerge(
                        "font-medium",
                        isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'
                      )}>
                        {option.name || option.label}
                      </span>
                      {option.email && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {option.email}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })
            ) : (
              <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                {searchQuery ? 'Nenhum usuário encontrado' : 'Nenhuma opção disponível'}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
